
import { useCallback, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { realtimeCentral } from '@/core/RealtimeCentral';
import { debugLogger } from '@/utils/debugLogger';

/**
 * Hook to listen for real-time connection updates using RealtimeCentral
 */
export function useConnectionsRealtime(onConnectionChanged: () => void) {
  const { user } = useAuth();
  
  useEffect(() => {
    // Skip if no user or if realtime system not activated
    if (!user?.id || !realtimeCentral.isActive()) {
      return;
    }

    debugLogger.debug('useConnectionsRealtime', 'Setting up real-time subscriptions for user:', user.id);

    // Subscribe to connection changes using the centralized system
    const unsubscribe = realtimeCentral.subscribe(`connections-${user.id}`, {
      id: `connections-${user.id}`,
      onNotification: (notification) => {
        debugLogger.debug('useConnectionsRealtime', 'Received notification:', notification);
        
        // If it's a connection-related notification, refresh connections data
        if (notification.category === 'connection') {
          debugLogger.debug('useConnectionsRealtime', 'Connection-related notification, refreshing data');
          onConnectionChanged();
        }
      }
    });

    return () => {
      debugLogger.debug('useConnectionsRealtime', 'Cleaning up connections realtime subscriptions');
      unsubscribe();
    };
  }, [user?.id, onConnectionChanged]);

  return null;
}
